/* eslint-disable no-underscore-dangle */
import {
  APIReference_apiReference as APIReference,
  APIClassReference,
  APIReference_apiReference_DatatypeReference as APIDatatypeReference,
  APIReference_apiReference_EnumReference as APIEnumReference,
  APIReference_apiReference_GlobalReference as APIGlobalReference,
  APIReference_apiReference_LibraryReference as APILibraryReference,
  APIReference_apiReference_ClassReference_property_security as ClassReferencePropertiesSecurity,
  APIReference_apiReference_DatatypeReference_mathOperations as APIDatatypeMathOperation,
  ReferenceType,
  MemberType,
  ClassReferenceParent,
} from '@modules/client';
import HeadingItem from '@modules/utilities/interfaces/HeadingItem';
import { useTranslation } from '@rbx/intl';
import { logOnServer } from '@modules/utilities/utils/log';
import { ApiTag, EngineTag, SecurityTag } from '@modules/ui/tags/types';
import type {
  APIReferenceCallbackList,
  APIReferenceEventList,
  APIReferenceMethodList,
  APIReferencePropertyList,
  ClassReference,
  DatatypeReferenceConstructorList,
  DatatypeReferenceFunctionList,
  DatatypeReferenceMethodList,
  DatatypeReferencePropertyList,
  DatatypeReferenceMathOperationList,
  GlobalReferenceFunctionList,
  GlobalReferencePropertyList,
  LibraryReferenceFunctionList,
  LibraryReferencePropertyList,
  ParentClassReference,
  CharacteristicsRowTypes,
} from '../types';
import {
  callbacksID,
  codeSamplesID,
  constructorsID,
  eventsID,
  functionsID,
  itemsID,
  methodsID,
  propertiesID,
  summaryID,
  mathOperationsID,
  REGEX_INHERIT_INSTANCE,
  REGEX_INHERIT_OBJECT,
} from '../constants/apiReferenceConstants';

import { getClassRowName } from './characteristicsRowUtils';
import { getMathOperationSlug } from './apiReferencePageNavUtils';
import { securityTagToInt, hasDeprecatedTag } from '../sharedUI/utils/tagUtils';

// TODO: Refactor this file
// https://jira.rbx.com/browse/CRK-916

export const isInheritInstanceIteratorKey = (key: string) => REGEX_INHERIT_INSTANCE.test(key);
export const isInheritObjectIteratorKey = (key: string) => REGEX_INHERIT_OBJECT.test(key);

// APIReference methods
export const isParentClassReference = (
  row: APIReference | ParentClassReference,
): row is ParentClassReference => {
  // eslint-disable-next-line no-underscore-dangle
  return row.__typename === 'ClassReference';
};

export const isClassReference = (
  row: ClassReferenceParent | APIReference | APIClassReference,
): row is APIClassReference => {
  // eslint-disable-next-line no-underscore-dangle
  return row.__typename === 'ClassReference';
};

export const isDatatypeReference = (row: APIReference): row is APIDatatypeReference => {
  // eslint-disable-next-line no-underscore-dangle
  return row.__typename === 'DatatypeReference';
};

export const isGlobalReference = (row: APIReference): row is APIGlobalReference => {
  // eslint-disable-next-line no-underscore-dangle
  return row.__typename === 'GlobalReference';
};

export const isLibraryReference = (row: APIReference): row is APILibraryReference => {
  // eslint-disable-next-line no-underscore-dangle
  return row.__typename === 'LibraryReference';
};

export const isEnumReference = (row: APIReference): row is APIEnumReference => {
  // eslint-disable-next-line no-underscore-dangle
  return row.__typename === 'EnumReference';
};

export const getReferenceType = (reference: APIReference): ReferenceType => {
  // eslint-disable-next-line no-underscore-dangle
  switch (reference.__typename) {
    case 'ClassReference':
      return ReferenceType.CLASS;
    case 'DatatypeReference':
      return ReferenceType.DATATYPE;
    case 'EnumReference':
      return ReferenceType.ENUM;
    case 'GlobalReference':
      return ReferenceType.GLOBAL;
    case 'LibraryReference':
      return ReferenceType.LIBRARY;
    default:
      throw new Error('Unexpected reference type.');
  }
};

export const containsCallbacks = (parents: ParentClassReference[] | null) => {
  if (
    parents?.find((parent) => {
      return parent.callbacks !== null && parent.callbacks.length > 0;
    })
  ) {
    return true;
  }
  return (
    parents?.find((parent) => parent.callbacks !== null && parent.callbacks.length > 0) != null
  );
};

export const containsHiddenTags = (
  tags?: ApiTag[] | null,
  security?: SecurityTag[] | ClassReferencePropertiesSecurity | null,
) => {
  if (tags?.includes(ApiTag.Hidden)) {
    return true;
  }
  if (Array.isArray(security)) {
    return security.some(
      (tag) =>
        tag === SecurityTag.RobloxScriptSecurity ||
        tag === SecurityTag.RobloxSecurity ||
        tag === SecurityTag.RobloxEngineSecurity,
    );
  }
  return (
    security?.read === SecurityTag.RobloxScriptSecurity ||
    security?.read === SecurityTag.RobloxSecurity ||
    security?.read === SecurityTag.RobloxEngineSecurity ||
    security?.write === SecurityTag.RobloxScriptSecurity ||
    security?.write === SecurityTag.RobloxSecurity ||
    security?.write === SecurityTag.RobloxEngineSecurity
  );
};

// Check is the member should be displayed
// Security Priority: None < PluginSecurity < LocalUserSecurity < RobloxScriptSecurity < RobloxSecurity < NotAccessibleSecurity
function innerMemberShouldBeDisplayed(
  memberType: MemberType,
  isPublic: boolean,
  isScriptable: boolean,
  securityTag?: SecurityTag | null,
) {
  // Member default is not Visibility
  let visibility = false;

  // If securityTag is null, not Visibility
  if (!securityTag) return visibility;

  // API does NOT have "Hidden" tag => isPublic
  // API does NOT have "NotScriptable" tag => isScriptable

  if (memberType === MemberType.Property) {
    // Property Implementation
    /*
       if (API does NOT have "Hidden" tag)
          return security <= RobloxSecurity
       if (API does NOT have "NotScriptable" tag)
          return security <= PluginSecurity
       return false
    */
    if (isPublic) {
      visibility =
        securityTagToInt(securityTag) <= securityTagToInt(SecurityTag.RobloxSecurity) ||
        securityTagToInt(securityTag) <= securityTagToInt(SecurityTag.RobloxEngineSecurity);
    } else if (isScriptable) {
      visibility = securityTagToInt(securityTag) <= securityTagToInt(SecurityTag.PluginSecurity);
    }
  } else if (memberType === MemberType.Event) {
    // Event Implementation
    // return (API does NOT have "NotScriptable" tag) && security <= PluginSecurity
    visibility =
      isScriptable && securityTagToInt(securityTag) <= securityTagToInt(SecurityTag.PluginSecurity);
  } else {
    // Default Member Implementation
    // return security <= PluginSecurity
    visibility = securityTagToInt(securityTag) <= securityTagToInt(SecurityTag.PluginSecurity);
  }

  return visibility;
}

export const memberShouldBeDisplayed = (
  referenceType: ReferenceType,
  memberType: MemberType,
  tags?: ApiTag[] | null,
  securityBlock?: SecurityTag[] | ClassReferencePropertiesSecurity | null,
) => {
  // According to https://roblox.atlassian.net/wiki/spaces/~bsharp/pages/1805750763/Reflection+API+Visibility(New)

  if (referenceType !== ReferenceType.CLASS) {
    return true;
  }
  // Extract tags information
  const isPublic = !tags?.includes(ApiTag.Hidden);
  const isScriptable = !tags?.includes(ApiTag.NotScriptable);

  // if securityBlock is null, then do not show
  if (!securityBlock) return false;

  // if securityBlock is array type, SecurityTag[]
  // for method, event, callback. see https://github.com/Roblox/creator-docs-staging/blob/1657414890c6500266a38cd31ed4340b9b615f84/tools/schemas/engine/classes.json
  if (Array.isArray(securityBlock)) {
    if (securityBlock.length > 0) {
      const [securityTag] = securityBlock;
      return innerMemberShouldBeDisplayed(memberType, isPublic, isScriptable, securityTag);
    }
    return false;
  }
  // securityBlock is object type, ClassReferencePropertiesSecurity
  // for class's property. see https://github.com/Roblox/creator-docs-staging/blob/1657414890c6500266a38cd31ed4340b9b615f84/tools/schemas/engine/classes.json
  const classReadSecurityTag = securityBlock.read;
  const classWriteSecurityTag = securityBlock.write;
  return (
    innerMemberShouldBeDisplayed(memberType, isPublic, isScriptable, classReadSecurityTag) ||
    innerMemberShouldBeDisplayed(memberType, isPublic, isScriptable, classWriteSecurityTag)
  );
};

type APIReferenceCharacteristicList =
  | APIReferencePropertyList
  | APIReferenceMethodList
  | APIReferenceEventList
  | APIReferenceCallbackList;

type GlobalOrLibraryCharacteristicList =
  | GlobalReferencePropertyList
  | LibraryReferencePropertyList
  | GlobalReferenceFunctionList
  | LibraryReferenceFunctionList;

type DatatypeCharacteristicList =
  | DatatypeReferencePropertyList
  | DatatypeReferenceConstructorList
  | DatatypeReferenceFunctionList
  | DatatypeReferenceMethodList
  | DatatypeReferenceMathOperationList;

type propagatorOptions = {
  showDeprecated: boolean;
  level: number;
  delimiter: ':' | '.';
};

const allowedDuplicateMemberNames = ['new'];

export const getMathOperationDisplayName = (mathOperation: APIDatatypeMathOperation) => {
  return `${mathOperation.typeA} ${mathOperation.operation} ${mathOperation.typeB}`;
};

const processIterator = (
  headings: HeadingItem[],
  item: Exclude<
    APIReferenceCharacteristicList | DatatypeCharacteristicList | GlobalOrLibraryCharacteristicList,
    null
  >[0],
  delimiter: string,
  level: number,
  showDeprecated: boolean,
  characteristicNames: Set<string>,
  // options: propagatorOptions,
) => {
  if (showDeprecated || (!showDeprecated && !hasDeprecatedTag(item.tags))) {
    // special handling for datatype - mathoperation type coercion
    const trimmedName =
      'operation' in item
        ? getMathOperationDisplayName(item)
        : item.name.substring(item.name.indexOf(delimiter) + 1);
    const slugName = 'operation' in item ? getMathOperationSlug(item) : trimmedName;
    if (characteristicNames.has(slugName)) {
      if (!allowedDuplicateMemberNames.includes(slugName)) {
        logOnServer(`Duplicate slugName: ${slugName}`);
      }
      return;
    }
    characteristicNames.add(slugName);
    const isDeprecated =
      hasDeprecatedTag(item.tags) || ('deprecationMessage' in item && !!item.deprecationMessage);
    headings.push({
      title: trimmedName,
      slug: slugName,
      level,
      isDeprecated,
    });
  }
};

function propagateHeadingIterator(
  iterator: HeadingItem[],
  characteristicList:
    | APIReferenceCharacteristicList
    | DatatypeCharacteristicList
    | GlobalOrLibraryCharacteristicList,
  options: propagatorOptions,
) {
  if (characteristicList) {
    const characteristicNames = new Set<string>();
    characteristicList.forEach((item) =>
      processIterator(
        iterator,
        item,
        options.delimiter,
        options.level,
        options.showDeprecated,
        characteristicNames,
      ),
    );
  }
}

export enum IteratorName {
  // keyed by the member's collection field name
  Properties = 'properties',
  Events = 'events',
  Methods = 'methods',
  Callbacks = 'callbacks',
}

export const IteratorNameToMemberType = {
  [IteratorName.Properties]: MemberType.Property,
  [IteratorName.Events]: MemberType.Event,
  [IteratorName.Methods]: MemberType.Method,
  [IteratorName.Callbacks]: MemberType.Callback,
};

// TODO: DRY up IteratorName, MemberType, preferring MemberType
// throughout all api logic. For now, translate types via this map
export const classMemberTypeToIteratorName: Record<ClassMemberType, IteratorName> = {
  [MemberType.Property]: IteratorName.Properties,
  [MemberType.Event]: IteratorName.Events,
  [MemberType.Method]: IteratorName.Methods,
  [MemberType.Callback]: IteratorName.Callbacks,
};

function getIterator(apiReference: ParentClassReference, iteratorName: IteratorName) {
  if (iteratorName === IteratorName.Properties) {
    return apiReference.properties;
  }
  if (iteratorName === IteratorName.Events) {
    return apiReference.events;
  }
  if (iteratorName === IteratorName.Methods) {
    return apiReference.methods;
  }
  if (iteratorName === IteratorName.Callbacks) {
    return apiReference.callbacks;
  }
  throw Error(`Invalid iterator name ${iteratorName}`);
}

export function isInheritIteratorKey(key: string) {
  return /^\w+-inherit-/.test(key);
}

export function isInheritNodeId(nodeId: string) {
  return /\/\w+-inherit-\w+\//.test(nodeId);
}

export function getInheritIteratorKey(iteratorName: IteratorName, parentName: string) {
  return `${iteratorName}-inherit-${parentName}`;
}

function propagateHeadingIteratorWithParent(
  characteristicList: APIReferenceCharacteristicList | DatatypeCharacteristicList,
  parentAPIReferences: ParentClassReference[] | null,
  iteratorName: IteratorName,
  options: propagatorOptions,
  translate: ReturnType<typeof useTranslation>['translate'],
) {
  const headings: HeadingItem[] = [];
  if (characteristicList === null) return headings;
  propagateHeadingIterator(headings, characteristicList, options);
  parentAPIReferences?.forEach((parent) => {
    const iterator = getIterator(parent, iteratorName);
    if (iterator && iterator.length > 0) {
      const characteristicNames = new Set<string>();
      headings.push({
        title:
          translate('Label.InheritedFromName', {
            name: parent.name,
          }) || `Inherited from ${parent.name}`,
        slug: getInheritIteratorKey(iteratorName, parent.name),
        level: options.level,
      });
      iterator.forEach((item) =>
        processIterator(
          headings,
          item,
          options.delimiter,
          options.level + 1,
          options.showDeprecated,
          characteristicNames,
        ),
      );
    }
  });
  return headings;
}

type APIReferenceParentCharacteristics = {
  properties: NonNullable<APIReferencePropertyList>;
  events: NonNullable<APIReferenceEventList>;
  methods: NonNullable<APIReferenceMethodList>;
  callbacks: NonNullable<APIReferenceCallbackList>;
};

function flattenParentCharacteristics(
  parentAPIReferences: ParentClassReference[] | null,
): APIReferenceParentCharacteristics {
  const defaultResult: APIReferenceParentCharacteristics = {
    properties: [],
    events: [],
    methods: [],
    callbacks: [],
  };

  if (parentAPIReferences == null) {
    return defaultResult;
  }

  return parentAPIReferences.reduce<APIReferenceParentCharacteristics>((prevResult, currParent) => {
    const {
      properties: prevProps,
      events: prevEvents,
      methods: prevMethods,
      callbacks: prevCallbacks,
    } = prevResult;
    const {
      properties: currProps,
      events: currEvents,
      methods: currMethods,
      callbacks: currCallbacks,
    } = currParent;

    return {
      ...prevResult,
      properties: prevProps.concat(currProps ?? []),
      events: prevEvents.concat(currEvents ?? []),
      methods: prevMethods.concat(currMethods ?? []),
      callbacks: prevCallbacks.concat(currCallbacks ?? []),
    };
  }, defaultResult);
}

export const isFieldDeprecated = (row: {
  tags: EngineTag[] | null;
  deprecatedMessage?: string | null;
}) => hasDeprecatedTag(row.tags) || !!row.deprecatedMessage;

const isFieldNameDeprecated = (row: CharacteristicsRowTypes, fieldName: string) => {
  return getClassRowName(row) === fieldName && isFieldDeprecated(row);
};

function hasCharacteristic(
  ownCharacteristic:
    | APIReferenceCharacteristicList
    | GlobalOrLibraryCharacteristicList
    | DatatypeCharacteristicList,
  parentCharacteristic: APIReferenceCharacteristicList,
) {
  return (
    (Array.isArray(ownCharacteristic) && ownCharacteristic.length > 0) ||
    (Array.isArray(parentCharacteristic) && parentCharacteristic.length > 0)
  );
}

const hasNonDeprecatedCharacteristic = (
  characteristics: { tags: ApiTag[] | null }[],
  showDeprecated: boolean,
) => {
  return showDeprecated
    ? true
    : characteristics.some((characteristic) => !isFieldDeprecated(characteristic));
};

function datatypeHasCharacteristic(
  characteristicList: DatatypeCharacteristicList,
  showDeprecated: boolean,
) {
  return (
    Array.isArray(characteristicList) &&
    characteristicList.length > 0 &&
    hasNonDeprecatedCharacteristic(characteristicList, showDeprecated)
  );
}

// Special function to check if callbacks should be shown
// If showDeprecated is false, only show callbacks if not every callback is deprecated
export const showClassCallbacks = (
  ownCallbacks: APIReferenceCallbackList,
  parent: ParentClassReference[] | null,
) => {
  const parentCallbacks = flattenParentCharacteristics(parent).callbacks;
  return (
    (Array.isArray(ownCallbacks) && ownCallbacks.length > 0) ||
    (Array.isArray(parentCallbacks) && parentCallbacks.length > 0)
  );
};

const resolveDeprecatedFields = <T extends { tags: ApiTag[] | null }>(fields: T[] | null): T[] => {
  if (Array.isArray(fields)) {
    return fields.filter((row) => !isFieldDeprecated(row));
  }
  return [];
};

export const resolveDeprecatedClass = (classReference: APIClassReference): APIClassReference => {
  return {
    ...classReference,
    properties: resolveDeprecatedFields(classReference.properties),
    methods: resolveDeprecatedFields(classReference.methods),
    events: resolveDeprecatedFields(classReference.events),
    callbacks: resolveDeprecatedFields(classReference.callbacks),
  };
};

export const resolveDeprecatedGlobalOrLibrary = (
  reference: APIGlobalReference | APILibraryReference,
): APIGlobalReference | APILibraryReference => {
  return {
    ...reference,
    properties: resolveDeprecatedFields(reference.properties),
    functions: resolveDeprecatedFields(reference.functions),
  };
};
export const resolveDeprecatedDatatype = (
  reference: APIDatatypeReference,
): APIDatatypeReference => {
  return {
    ...reference,
    constructors: resolveDeprecatedFields(reference.constructors),
    properties: resolveDeprecatedFields(reference.properties),
    methods: resolveDeprecatedFields(reference.methods),
    functions: resolveDeprecatedFields(reference.functions),
    mathOperations: resolveDeprecatedFields(reference.mathOperations),
  };
};

export const containsDeprecatedField = (
  apiReference: APIReference | ClassReferenceParent,
  feildName: string,
): boolean => {
  if (isClassReference(apiReference)) {
    return !!(
      apiReference.properties?.find((field) => isFieldNameDeprecated(field, feildName)) ||
      apiReference.methods?.find((field) => isFieldNameDeprecated(field, feildName)) ||
      apiReference.events?.find((field) => isFieldNameDeprecated(field, feildName)) ||
      apiReference.callbacks?.find((field) => isFieldNameDeprecated(field, feildName))
    );
  }
  return false;
};

export const getAPIReferenceHeadings = (
  translate: ReturnType<typeof useTranslation>['translate'],
  reference: APIReference,
  parents: ClassReferenceParent[] | null,
  showDeprecated: boolean,
) => {
  const heading: HeadingItem[] = [];

  if (isClassReference(reference)) {
    const { properties, events, methods, callbacks } = reference;
    const {
      properties: parentProperties,
      events: parentEvents,
      methods: parentMethods,
      callbacks: parentCallbacks,
    } = flattenParentCharacteristics(parents);
    const hasProperties = hasCharacteristic(properties, parentProperties);
    const hasEvents = hasCharacteristic(events, parentEvents);
    const hasMethods = hasCharacteristic(methods, parentMethods);
    const hasCallbacks = hasCharacteristic(callbacks, parentCallbacks);

    // summary
    if (hasProperties || hasEvents || hasMethods || hasCallbacks) {
      heading.push({ title: translate('Label.Summary') || 'Summary', slug: summaryID, level: 2 });
    }

    if (hasProperties) {
      heading.push({
        title: translate('Label.Properties') || 'Properties',
        slug: `${summaryID}-${propertiesID}`,
        level: 3,
      });
    }
    if (hasMethods) {
      heading.push({
        title: translate('Label.Methods') || 'Methods',
        slug: `${summaryID}-${methodsID}`,
        level: 3,
      });
    }
    if (hasEvents) {
      heading.push({
        title: translate('Label.Events') || 'Events',
        slug: `${summaryID}-${eventsID}`,
        level: 3,
      });
    }

    if (hasCallbacks) {
      heading.push({
        title: translate('Label.Callbacks') || 'Callbacks',
        slug: `${summaryID}-${callbacksID}`,
        level: 3,
      });
    }

    // iterators
    const propertyIterator = propagateHeadingIteratorWithParent(
      properties,
      parents,
      IteratorName.Properties,
      {
        showDeprecated,
        level: 3,
        delimiter: '.',
      },
      translate,
    );
    const eventIterator = propagateHeadingIteratorWithParent(
      events,
      parents,
      IteratorName.Events,
      {
        showDeprecated,
        level: 3,
        delimiter: '.',
      },
      translate,
    );
    const methodsIterator = propagateHeadingIteratorWithParent(
      methods,
      parents,
      IteratorName.Methods,
      {
        showDeprecated,
        level: 3,
        delimiter: ':',
      },
      translate,
    );
    const callbackIterator = propagateHeadingIteratorWithParent(
      callbacks,
      parents,
      IteratorName.Callbacks,
      {
        showDeprecated,
        level: 3,
        delimiter: '.',
      },
      translate,
    );

    if (propertyIterator.length > 0) {
      heading.push({
        title: translate('Label.Properties') || 'Properties',
        slug: propertiesID,
        level: 2,
      });
      heading.push(...propertyIterator);
    }
    if (methodsIterator.length > 0) {
      heading.push({ title: translate('Label.Methods') || 'Methods', slug: methodsID, level: 2 });
      heading.push(...methodsIterator);
    }
    if (eventIterator.length > 0) {
      heading.push({ title: translate('Label.Events') || 'Events', slug: eventsID, level: 2 });
      heading.push(...eventIterator);
    }

    // pages may not contain callbacks
    if (callbackIterator.length > 0 || containsCallbacks(parents)) {
      heading.push({
        title: translate('Label.Callbacks') || 'Callbacks',
        slug: callbacksID,
        level: 2,
      });
      heading.push(...callbackIterator);
    }
  }

  if (isEnumReference(reference)) {
    heading.push({ title: translate('Label.Items') || 'Items', slug: itemsID, level: 2 });
    if (Array.isArray(reference.codeSamples) && reference.codeSamples.length > 0) {
      heading.push({
        title: translate('Label.CodeSamples') || 'Code Samples',
        slug: codeSamplesID,
        level: 2,
      });
    }
  }

  if (isDatatypeReference(reference)) {
    const { properties, functions, constructors, methods, mathOperations } = reference;
    const hasProperties = datatypeHasCharacteristic(properties, showDeprecated);
    const hasFunctions = datatypeHasCharacteristic(functions, showDeprecated);
    const hasConstructors = datatypeHasCharacteristic(constructors, showDeprecated);
    const hasMethods = datatypeHasCharacteristic(methods, showDeprecated);
    const hasMathOperations = datatypeHasCharacteristic(mathOperations, showDeprecated);

    // summary
    if (hasProperties || hasFunctions || hasConstructors || hasMethods) {
      heading.push({ title: translate('Label.Summary') || 'Summary', slug: summaryID, level: 2 });
    }

    if (hasConstructors) {
      heading.push({
        title: translate('Label.Constructors') || 'Constructors',
        slug: `${summaryID}-${constructorsID}`,
        level: 3,
      });
    }
    if (hasProperties) {
      heading.push({
        title: translate('Label.Properties') || 'Properties',
        slug: `${summaryID}-${propertiesID}`,
        level: 3,
      });
    }
    if (hasMethods) {
      heading.push({
        title: translate('Label.Methods') || 'Methods',
        slug: `${summaryID}-${methodsID}`,
        level: 3,
      });
    }
    if (hasFunctions) {
      heading.push({
        title: translate('Label.Functions') || 'Functions',
        slug: `${summaryID}-${functionsID}`,
        level: 3,
      });
    }
    if (hasMathOperations) {
      heading.push({
        title: translate('Label.MathOperations') || 'Math Operations',
        slug: `${summaryID}-${mathOperationsID}`,
        level: 3,
      });
    }

    // iterators
    const constructorsIterator: HeadingItem[] = [];
    const propertiesIterator: HeadingItem[] = [];
    const methodsIterator: HeadingItem[] = [];
    const functionsIterator: HeadingItem[] = [];
    const mathOperationsIterator: HeadingItem[] = [];

    propagateHeadingIterator(constructorsIterator, constructors, {
      showDeprecated,
      level: 3,
      delimiter: '.',
    });
    propagateHeadingIterator(propertiesIterator, properties, {
      showDeprecated,
      level: 3,
      delimiter: '.',
    });
    propagateHeadingIterator(methodsIterator, methods, {
      showDeprecated,
      level: 3,
      delimiter: ':',
    });
    propagateHeadingIterator(functionsIterator, functions, {
      showDeprecated,
      level: 3,
      delimiter: '.',
    });
    propagateHeadingIterator(mathOperationsIterator, mathOperations, {
      showDeprecated,
      level: 3,
      delimiter: ':',
    });

    if (constructorsIterator.length > 0) {
      heading.push({
        title: translate('Label.Constructors') || 'Constructors',
        slug: constructorsID,
        level: 2,
      });
      heading.push(...constructorsIterator);
    }
    if (propertiesIterator.length > 0) {
      heading.push({
        title: translate('Label.Properties') || 'Properties',
        slug: propertiesID,
        level: 2,
      });
      heading.push(...propertiesIterator);
    }
    if (methodsIterator.length > 0) {
      heading.push({ title: translate('Label.Methods') || 'Methods', slug: methodsID, level: 2 });
      heading.push(...methodsIterator);
    }
    if (functionsIterator.length > 0) {
      heading.push({
        title: translate('Label.Functions') || 'Functions',
        slug: functionsID,
        level: 2,
      });
      heading.push(...functionsIterator);
    }
    if (mathOperationsIterator.length > 0) {
      heading.push({
        title: translate('Label.MathOperations') || 'Math Operations',
        slug: mathOperationsID,
        level: 2,
      });
      heading.push(...mathOperationsIterator);
    }
  }

  if (isGlobalReference(reference) || isLibraryReference(reference)) {
    const { properties, functions } = reference;
    const hasProperties = hasCharacteristic(properties, null);
    const hasFunctions = hasCharacteristic(functions, null);
    // summary
    if (hasProperties || hasFunctions) {
      heading.push({ title: translate('Label.Summary') || 'Summary', slug: summaryID, level: 2 });
    }

    if (hasFunctions) {
      heading.push({
        title: translate('Label.Functions') || 'Functions',
        slug: `${summaryID}-${functionsID}`,
        level: 3,
      });
    }
    if (hasProperties) {
      heading.push({
        title: translate('Label.Properties') || 'Properties',
        slug: `${summaryID}-${propertiesID}`,
        level: 3,
      });
    }

    const functionsIterator: HeadingItem[] = [];
    const propertiesIterator: HeadingItem[] = [];

    propagateHeadingIterator(functionsIterator, functions, {
      showDeprecated,
      level: 3,
      delimiter: '.',
    });

    propagateHeadingIterator(propertiesIterator, properties, {
      showDeprecated,
      level: 3,
      delimiter: '.',
    });

    if (functionsIterator.length > 0) {
      heading.push({
        title: translate('Label.Functions') || 'Functions',
        slug: functionsID,
        level: 2,
      });
      heading.push(...functionsIterator);
    }

    if (propertiesIterator.length > 0) {
      heading.push({
        title: translate('Label.Properties') || 'Properties',
        slug: propertiesID,
        level: 2,
      });
      heading.push(...propertiesIterator);
    }
  }

  return heading;
};

export const removeHiddenSections = <
  T extends {
    tags?: ApiTag[] | null;
    security?: SecurityTag[] | ClassReferencePropertiesSecurity | null;
  },
>(
  referenceType: ReferenceType,
  memberType: MemberType,
  sections: T[] | null,
) => {
  return (
    sections?.filter((section: T) =>
      memberShouldBeDisplayed(referenceType, memberType, section.tags, section.security),
    ) ?? null
  );
};

export const classReferenceWithoutHiddenSections = (classReference: ClassReference) => {
  return {
    ...classReference,
    properties: removeHiddenSections(
      ReferenceType.CLASS,
      MemberType.Property,
      classReference.properties,
    ),
    methods: removeHiddenSections(ReferenceType.CLASS, MemberType.Method, classReference.methods),
    events: removeHiddenSections(ReferenceType.CLASS, MemberType.Event, classReference.events),
    callbacks: removeHiddenSections(
      ReferenceType.CLASS,
      MemberType.Callback,
      classReference.callbacks,
    ),
  };
};

// deal with Class.Studio members
// e.g. Auto-Recovery Interval (Minutes)
// Auto-Recovery Path
// Built-in Function Color
// Select/Hover Color
export const getMemberSlug = (name: string): string => {
  const res = name
    .replace(/"|\(|\)/g, '')
    .replace(/\//g, '_')
    .replace(/\s/g, '-');
  if (
    !res.match(/^[\w-]+$/) &&
    // exclude math operations
    !res.match(/\s(\*|\+|-|\/)\s$/) &&
    process.env.environment !== 'production'
  ) {
    // eslint-disable-next-line no-console
    console.warn('warning invalid member name:', name);
  }
  return res;
};

export const getShortMemberName = (name: string) => {
  const splits = name.split(/\.|:/);
  if (splits.length === 1) {
    // eslint-disable-next-line prefer-destructuring
    return splits[0].trim();
  }
  if (splits.length === 2) {
    // eslint-disable-next-line prefer-destructuring
    return splits[1].trim();
  }
  throw Error(`invalid member name: ${name}`);
};

export const getShortMemberSlug = (name: string) => {
  return getMemberSlug(getShortMemberName(name));
};

export type ClassMemberType =
  | MemberType.Property
  | MemberType.Method
  | MemberType.Event
  | MemberType.Callback;
// TODO: can define *MemberType types once we iterate on other member type details components

// order by which sections should appear in reference summary or details, if they are defined
export const orderedMemberTypes = (
  referenceType: ReferenceType,
): MemberType[] | ClassMemberType[] => {
  switch (referenceType) {
    case ReferenceType.CLASS:
      return [
        MemberType.Property,
        MemberType.Method,
        MemberType.Event,
        MemberType.Callback,
      ] as ClassMemberType[];
    case ReferenceType.DATATYPE:
      return [
        MemberType.Constructor,
        MemberType.Property,
        MemberType.Method,
        MemberType.Function,
        MemberType.MathOperation,
      ];
    case ReferenceType.GLOBAL:
    case ReferenceType.LIBRARY:
      return [MemberType.Function, MemberType.Property];
    default:
      throw new Error(`Unhandled reference type: ${referenceType}`);
  }
};

// Member types that should display with parentheses ()
export const MEMBER_TYPES_WITH_PARENTHESIS: Set<MemberType> = new Set([
  MemberType.Callback,
  MemberType.Constructor,
  MemberType.Event,
  MemberType.Function,
  MemberType.Method,
]);
